import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api, { apiError } from "@/lib/api";
import { TRY, NUM, unitLabel } from "@/lib/format";
import { discountFor, productDiscountFields } from "@/lib/discountKey";
import { contactPosMode, manualNetFor, POS_MODE_LABEL } from "@/lib/posPrice";
import ProductPicker from "@/components/ProductPicker";
import BarcodeScanner from "@/components/BarcodeScanner";
import BarcodeInput from "@/components/BarcodeInput";
import NewProductQuickDialog from "@/components/NewProductQuickDialog";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, FileSpreadsheet, Download, Truck, ShieldCheck, Package, ScanLine, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { setScanPageHandler } from "@/hooks/useGlobalScanner";

const UNITS = ["ADET", "METRE", "KİLO", "PAKET", "KUTU", "TOP", "RULO", "METRETÜL"];
export const blankEntryItem = () => ({ name: "", description: "", quantity: 1, unit: "ADET", unit_price: 0, discount_rate: 0 });
const num = (v) => (typeof v === "number" ? v : (parseFloat(String(v ?? "").replace(/[^0-9.,-]/g, "").replace(",", ".")) || 0));

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);

// Satır bileşeni memo'lu: bir hücreye yazarken yalnızca o satır yeniden çizilir (yüksek kalemli kayıtlarda donma yok)
const EntryRow = memo(function EntryRow({ it, i, onChange, onRemove, onPick, onNameKeyDown, isAlis, posMode }) {
  const net = Number(it.unit_price || 0) * (1 - Number(it.discount_rate || 0) / 100);
  const line = net * Number(it.quantity || 0);
  const units = UNITS.includes(it.unit) ? UNITS : [it.unit, ...UNITS];
  const noPrice = isAlis && String(it.name || "").trim() && !(Number(it.unit_price) > 0);
  return (
    <tr className={`border-t ${noPrice ? "bg-amber-50/60" : ""}`} data-testid={`entry-row-${i}`}>
      <td className="p-1.5 text-center text-muted-foreground tabular-nums">{i + 1}</td>
      <td className="p-1.5">
        <div className="flex items-center gap-1">
          <Input className="h-9 flex-1 min-w-0" placeholder="Malzeme adı yazın, barkod okutun veya stoktan seçin" value={it.name} onChange={(e) => onChange(i, "name", e.target.value)} onKeyDown={(e) => onNameKeyDown(i, e)} data-testid={`entry-name-${i}`} />
          <ProductPicker label="" size="sm" variant={it.product_id ? "secondary" : "ghost"} onSelect={(p) => onPick(i, p)} testid={`entry-pick-${i}`} className="h-9 w-9 px-0 shrink-0" />
          {it.product_id ? <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 py-0.5" title="Stoktan seçildi" data-testid={`entry-stock-badge-${i}`}><PackageSearch className="h-2.5 w-2.5" />STOK</span> : null}
        </div>
        {!isAlis && it._manual && (
          <div className="text-[10px] text-orange-700 mt-0.5 pl-1" data-testid={`entry-manual-hint-${i}`}>Manuel fiyat ({POS_MODE_LABEL[posMode] || "Genel"}) · iskonto uygulanmaz</div>
        )}
        {isAlis && it.product_id && (it._stock != null || it._cost != null) && (
          <div className="text-[10px] text-muted-foreground mt-0.5 pl-1 tabular-nums" data-testid={`entry-stock-hint-${i}`}>
            Stok: <b>{NUM(it._stock ?? 0)}</b>{it._cost != null ? <> · mevcut maliyet <b>{TRY(it._cost)}</b>{Number(it.unit_price) > 0 && Number(it._cost) > 0 && Math.abs(Number(it.unit_price) - Number(it._cost)) > 0.005 ? <span className={Number(it.unit_price) > Number(it._cost) ? " text-destructive" : " text-emerald-700"}> ({Number(it.unit_price) > Number(it._cost) ? "+" : ""}{NUM(((Number(it.unit_price) - Number(it._cost)) / Number(it._cost)) * 100)}%)</span> : null}</> : null}
          </div>
        )}
      </td>
      <td className="p-1.5">
        <Select value={it.unit} onValueChange={(v) => onChange(i, "unit", v)}>
          <SelectTrigger className="h-9" data-testid={`entry-unit-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="p-1.5"><Input className="h-9 text-right tabular-nums" type="number" step="any" inputMode="decimal" value={it.quantity} onChange={(e) => onChange(i, "quantity", e.target.value)} data-testid={`entry-qty-${i}`} /></td>
      <td className="p-1.5"><Input className="h-9 text-right tabular-nums" type="number" step="any" inputMode="decimal" value={it.unit_price} onChange={(e) => onChange(i, "unit_price", e.target.value)} data-testid={`entry-price-${i}`} /></td>
      <td className="p-1.5"><Input className="h-9 text-right tabular-nums" type="number" step="any" inputMode="decimal" value={it.discount_rate} onChange={(e) => onChange(i, "discount_rate", e.target.value)} data-testid={`entry-disc-${i}`} /></td>
      <td className="p-1.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{TRY(net)}</td>
      <td className="p-1.5 text-right tabular-nums font-semibold whitespace-nowrap" data-testid={`entry-line-${i}`}>{TRY(line)}</td>
      <td className="p-1.5 text-center"><button type="button" onClick={() => onRemove(i)} className="text-destructive/70 hover:text-destructive" data-testid={`entry-rm-${i}`}><Trash2 className="h-4 w-4" /></button></td>
    </tr>
  );
});

/** Cari Ürün / Faturasız Alım kayıt editörü — form durumu yerel; üst sayfa (cari detay) her tuşta yeniden çizilmez. */
export default function CariEntryEditorDialog({ open, contact, initial, editId, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [np, setNp] = useState({ open: false, barcode: "" });   // bilinmeyen barkod → hızlı yeni ürün
  const discRef = useRef({ map: {}, def: 0 });
  const c = contact || {};

  useEffect(() => {
    if (!open || !initial) return;
    setForm({ ...initial, items: (initial.items || []).map((x) => ({ ...blankEntryItem(), ...x })) });
    discRef.current = { map: {}, def: 0 };
    // Cari ID + moduyla çöz → aynı ünvanlı mükerrer carilerde doğru iskonto (elektrikçi faturasız vs. müşteri) gelir.
    const mode = contactPosMode(c);
    api.get(`/discounts/resolve?contact_id=${encodeURIComponent(c.id || "")}&contact_title=${encodeURIComponent(c.title || "")}&mode=${mode}`)
      .then((r) => { discRef.current = { map: r.data.map || {}, def: Number(r.data.default || 0) }; })
      .catch(() => { discRef.current = { map: {}, def: 0 }; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const setItem = useCallback((i, k, v) => setForm((f) => ({ ...f, items: f.items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) })), []);
  const rmItem = useCallback((i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })), []);
  const addRows = (n) => setForm((f) => ({ ...f, items: [...f.items, ...Array.from({ length: n }, blankEntryItem)] }));

  // Stoktan seçilen ürün için satır kur (cari tipine göre fiyat/iskonto)
  const makeRow = (p, f) => {
    const isAlis = f.kind === "alis";
    // Cari tipine göre fiyat: manuel fiyatlı ürünlerde (ürün kartı) o modun manuel neti, iskonto %0; değilse liste + carinin iskontosu
    const manualNet = isAlis ? null : manualNetFor(p, contactPosMode(c));
    const dres = isAlis || manualNet != null ? null : discountFor(p, discRef.current.map);
    const disc = isAlis || manualNet != null ? 0 : (dres != null ? dres : Number(discRef.current.def || 0));
    const unitPrice = isAlis ? (p.cost || p.price || 0) : (manualNet != null ? manualNet : (p.price || 0));
    return { name: p.name, description: p.code || p.description || "", code: p.code || "", product_id: p.id, unit: unitLabel(p.unit, "ADET"), quantity: 1, unit_price: unitPrice, discount_rate: disc, ...productDiscountFields(p), ...(manualNet != null ? { _manual: true } : {}), ...(isAlis ? { _stock: p.quantity ?? null, _cost: p.cost ?? null } : {}) };
  };

  const addFromStock = (p) => setForm((f) => {
    if (!f) return f;
    const row = makeRow(p, f);
    const items = [...f.items];
    const idx = items.findIndex((it) => !String(it.name || "").trim());
    if (idx >= 0) items[idx] = row; else items.push(row);
    return { ...f, items };
  });

  // Satırdaki kutucuk: stoktan seçilen ürün O satıra yazılır (girilmiş adet korunur)
  const pickForRow = useCallback((i, p) => setForm((f) => {
    if (!f) return f;
    const row = makeRow(p, f);
    return { ...f, items: f.items.map((it, idx) => (idx === i ? { ...row, quantity: Number(it.quantity) > 0 ? it.quantity : 1 } : it)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [c.id, c.title, c.vat_exempt]);

  // Satırın ad kutusuna barkod okutulup Enter gelirse (okuyucu klavye gibi yazar) → ürün o satıra gelir
  const onNameKeyDown = useCallback((i, e) => {
    if (e.key !== "Enter") return;
    const code = String(e.currentTarget.value || "").trim();
    if (!/^\d{8,}$/.test(code)) return;
    e.preventDefault();
    resolveScanToRow(code, i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id, c.title, c.vat_exempt]);

  // Barkod okutma: ürün zaten varsa adet +1; yoksa boş satırı doldur ya da yeni satır ekle
  const addOrIncrementFromScan = (p) => setForm((f) => {
    if (!f) return f;
    const idx = f.items.findIndex((it) => it.product_id && it.product_id === p.id);
    if (idx >= 0) {
      toast.info(`${p.name} · adet ${Number(f.items[idx].quantity || 0) + 1}`);
      return { ...f, items: f.items.map((it, i) => (i === idx ? { ...it, quantity: Number(it.quantity || 0) + 1 } : it)) };
    }
    const row = makeRow(p, f);
    const items = [...f.items];
    const bi = items.findIndex((it) => !String(it.name || "").trim());
    if (bi >= 0) items[bi] = row; else items.push(row);
    toast.success(`Eklendi: ${p.name}`);
    return { ...f, items };
  });

  // Barkod çöz → ürünü ekle/artır; sonucu barkod kutusuna döndür (yeşil/kırmızı şerit için). Bulunamazsa UYARI.
  const lookupScan = async (code) => {
    const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(code)}&lite=true`);
    const exact = data.find((r) => [r.product.code, r.product.barcode, ...(r.product.barcodes || [])].some((b) => String(b || "").trim().toLowerCase() === String(code).trim().toLowerCase()));
    return { hit: exact || (data.length === 1 ? data[0] : null), many: data.length > 1 };
  };
  const resolveScan = async (code) => {
    try {
      const { hit, many } = await lookupScan(code);
      if (!hit) { toast.error(many ? `"${code}" birden fazla ürüne uyuyor — aramadan seçin` : `Barkod sistemde YOK: ${code}`); return { ok: false, label: many ? `Birden fazla: ${code}` : `Yok: ${code}` }; }
      addOrIncrementFromScan(hit.product);
      return { ok: true, label: hit.product.name };
    } catch (e) { toast.error(apiError(e)); return { ok: false, label: "Arama hatası" }; }
  };
  // Belirli satıra okutma (ad kutusunda barkod + Enter): ürün o satıra yazılır; bulunamazsa yazılan kod satırda kalır
  const resolveScanToRow = async (code, i) => {
    try {
      const { hit, many } = await lookupScan(code);
      if (!hit) { toast.error(many ? `"${code}" birden fazla ürüne uyuyor — aramadan seçin` : `Barkod sistemde YOK: ${code}`); return; }
      pickForRow(i, hit.product);
      toast.success(`Satır ${i + 1}: ${hit.product.name}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  // Diyalog açıkken fiziksel okuyucu (odakta yazı alanı yokken) ürün detayına gitmez → bu listeye ekler
  const scanRef = useRef(resolveScan);
  scanRef.current = resolveScan;
  useEffect(() => {
    if (!open) return undefined;
    return setScanPageHandler((code) => { scanRef.current(code); });
  }, [open]);

  const importExcel = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        const pick = (r, keys) => { for (const k of keys) { const kk = Object.keys(r).find((x) => x.toLocaleLowerCase("tr").trim() === k); if (kk != null && r[kk] !== "") return r[kk]; } return ""; };
        const newItems = rows.map((r) => ({
          name: String(pick(r, ["ürün adı", "urun adi", "ad", "isim", "ürün", "malzeme"]) || "").trim(),
          code: String(pick(r, ["kod", "ürün kodu"]) || "").trim(),
          description: "",
          unit: unitLabel(pick(r, ["birim"]), "ADET"),
          quantity: num(pick(r, ["miktar", "adet", "stok"])) || 1,
          unit_price: num(pick(r, ["birim fiyat", "fiyat", "liste fiyatı", "liste fiyati"])),
          discount_rate: num(pick(r, ["iskonto", "iskonto %", "indirim", "isk"])),
          product_id: "",
        })).filter((it) => it.name);
        if (!newItems.length) { toast.error("Geçerli kalem yok (Ürün Adı zorunlu)"); return; }
        setForm((f) => ({ ...f, items: [...f.items.filter((it) => String(it.name || "").trim()), ...newItems] }));
        toast.success(`${newItems.length} kalem eklendi`);
      } catch { toast.error("İçe aktarma hatası — sütun başlıklarını kontrol edin"); }
      finally { e.target.value = ""; }
    };
    reader.readAsArrayBuffer(file);
  };
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Ürün Adı", "Birim", "Miktar", "Birim Fiyat", "İskonto"],
      ["Örnek: Anahtar Işıklı", "ADET", 10, 192.5, 0],
      ["Örnek: 3x1.5 NYA Kablo", "METRE", 100, 25, 5],
    ]);
    ws["!cols"] = [{ wch: 34 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kalemler");
    XLSX.writeFile(wb, "cari-kalem-sablon.xlsx");
  };

  const totals = useMemo(() => {
    if (!form) return { sub: 0, kdv: 0, total: 0 };
    const sub = form.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0) * (1 - Number(i.discount_rate || 0) / 100), 0);
    const net = sub - sub * (Number(form.global_discount_rate || 0) / 100);
    const kdv = net * (c.vat_exempt ? 0 : 0.2);
    return { sub, kdv, total: net + kdv };
  }, [form, c.vat_exempt]);

  const save = async () => {
    const items = form.items.filter((i) => String(i.name || "").trim()).map((i) => {
      const clean = Object.fromEntries(Object.entries(i).filter(([k]) => !k.startsWith("_")));
      return { ...clean, quantity: Number(i.quantity), unit_price: Number(i.unit_price), discount_rate: Number(i.discount_rate || 0) };
    });
    if (!items.length) return toast.error("En az bir kalem girin");
    if (form.kind === "alis") {
      const zero = items.filter((i) => !(i.unit_price > 0)).length;
      const badQty = items.filter((i) => !(i.quantity > 0)).length;
      if (badQty) return toast.error(`${badQty} kalemin miktarı 0 veya eksi — düzeltin`);
      if (zero && !(await confirm(`${zero} kalemin alış fiyatı 0 ₺. Tedarikçiye borç yazılmayacak; yine de kaydedilsin mi?`))) return;
    }
    setSaving(true);
    try {
      const payload = { contact_title: c.title, date: form.date, note: form.note, doc_no: form.doc_no || "", global_discount_rate: Number(form.global_discount_rate || 0), items, kind: form.kind || "satis", add_to_stock: form.kind === "alis" ? !!form.add_to_stock : false };
      if (editId) await api.put(`/cari-entries/${editId}`, payload);
      else await api.post("/cari-entries", payload);
      toast.success(form.kind === "alis" ? "Faturasız alım kaydedildi" : "Cari ürün kaydı kaydedildi");
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const isAlis = form?.kind === "alis";
  const filledRows = form ? form.items.filter((i) => String(i.name || "").trim()) : [];
  const stockRows = filledRows.filter((i) => i.product_id).length;
  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-none w-[98vw] h-[96vh] max-h-[96vh] overflow-y-auto" data-testid="entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl flex items-center gap-3 flex-wrap">
            {isAlis && <span className="h-10 w-10 rounded-xl bg-secondary text-white flex items-center justify-center shrink-0"><Truck className="h-5 w-5" /></span>}
            <span>{isAlis ? (editId ? "Faturasız Alımı Düzenle" : "Faturasız Alım Girişi") : (editId ? "Cari Ürün Kaydını Düzenle" : "Cari Ürün / Kalem Ekle")}</span>
            {isAlis && (
              <span className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="text-[11px] font-normal">{c.title}</Badge>
                <Badge className="text-[11px] bg-emerald-500/10 text-emerald-700 border border-emerald-300 hover:bg-emerald-500/10" data-testid="entry-alis-badge"><ShieldCheck className="h-3 w-3 mr-1" />Açık Hesap · KDV'siz</Badge>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {form && (
          <div className="space-y-4">
            {isAlis && <p className="text-xs text-muted-foreground -mt-2">Faturası olmadan teslim alınan mallar. Kalem tutarları <b>tedarikçiye borç</b> olarak cari bakiyeye işlenir; ödeme yapıldığında "Ödeme Yap" ile düşülür.</p>}
            <div className={`grid gap-3 ${isAlis ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-2"}`}>
              <F label="Tarih"><DatePicker value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} data-testid="entry-date" /></F>
              {isAlis && <F label="İrsaliye / Belge No"><Input value={form.doc_no || ""} onChange={(e) => setForm((f) => ({ ...f, doc_no: e.target.value }))} placeholder="Tedarikçinin irsaliye veya fiş no (opsiyonel)" data-testid="entry-doc-no" /></F>}
              <F label="Genel İskonto (%)"><Input type="number" step="any" value={form.global_discount_rate} onChange={(e) => setForm((f) => ({ ...f, global_discount_rate: e.target.value }))} data-testid="entry-global-disc" /></F>
            </div>
            {isAlis && (
              <label className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${form.add_to_stock ? "border-emerald-300 bg-emerald-50/60" : "border-secondary/30 bg-secondary/5"}`} data-testid="entry-add-stock-row">
                <Switch checked={!!form.add_to_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, add_to_stock: v }))} data-testid="entry-add-stock" />
                <span className="text-sm flex-1"><b>Stoğa ekle</b> — stoktan seçilen ürünlerin adedi artar, maliyeti girilen net alış fiyatına güncellenir.</span>
                {form.add_to_stock && <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 whitespace-nowrap" data-testid="entry-stock-count"><Package className="h-3.5 w-3.5" />{stockRows} / {filledRows.length} kalem stoğa işlenecek</span>}
              </label>
            )}
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]" data-testid="entry-items">
                <thead>
                  <tr className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="p-2 text-left w-8">#</th>
                    <th className="p-2 text-left min-w-[300px]">Ürün Adı <span className="normal-case tracking-normal font-normal">· kutucuk: stoktan getir</span></th>
                    <th className="p-2 text-left w-28">Birim</th>
                    <th className="p-2 text-right w-20">Miktar</th>
                    <th className="p-2 text-right w-24">{isAlis ? "Alış Fiyatı" : "Birim Fiyat"}</th>
                    <th className="p-2 text-right w-24">İsk %</th>
                    <th className="p-2 text-right w-24">Net Fiyat</th>
                    <th className="p-2 text-right w-28">Tutar</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => <EntryRow key={i} it={it} i={i} onChange={setItem} onRemove={rmItem} onPick={pickForRow} onNameKeyDown={onNameKeyDown} isAlis={isAlis} posMode={contactPosMode(c)} />)}
                </tbody>
              </table>
            </div>
            <div className="flex items-start gap-2 flex-wrap">
              <BarcodeInput onResolve={resolveScan} onAddNew={(code) => setNp({ open: true, barcode: code })} placeholder="Barkod okut / kod + Enter" autoFocus testid="entry-barcode-input" className="w-full sm:w-[260px]" />
              <ProductPicker onSelect={addFromStock} label="Stoktan Ürün" testid="entry-product-picker" />
              <Button size="sm" variant="outline" onClick={() => setScanOpen(true)} data-testid="entry-scan-btn"><ScanLine className="h-4 w-4 mr-1.5" />Kamera</Button>
              <label className="cursor-pointer inline-flex items-center h-9 px-3 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium transition-colors" data-testid="entry-excel-label">
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel'den Yükle
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} data-testid="entry-excel" />
              </label>
              <Button size="sm" variant="ghost" onClick={downloadTemplate} data-testid="entry-template"><Download className="h-4 w-4 mr-1.5" />Şablon</Button>
              <Button size="sm" variant="outline" onClick={() => addRows(1)} data-testid="entry-add-row"><Plus className="h-3.5 w-3.5 mr-1" />Satır Ekle</Button>
              <Button size="sm" variant="ghost" onClick={() => addRows(5)} data-testid="entry-add-5rows"><Plus className="h-3.5 w-3.5 mr-1" />+5 Satır</Button>
            </div>
            <F label="Not"><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder={isAlis ? "Teslim alan, araç, eksik/hasarlı kalem vb." : ""} data-testid="entry-note" /></F>
            <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1 sm:max-w-xs sm:ml-auto">
              <div className="flex justify-between text-muted-foreground"><span>Ara Toplam</span><span className="tabular-nums">{TRY(totals.sub)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>KDV {c.vat_exempt ? (isAlis ? "(Açık Hesap · yok)" : "(muaf %0)") : "(%20)"}</span><span className="tabular-nums">{TRY(totals.kdv)}</span></div>
              <div className="flex justify-between pt-1 border-t font-head font-black"><span>{isAlis ? "Tedarikçiye Borç" : "Toplam"}</span><span className={`tabular-nums ${isAlis ? "text-secondary" : "text-primary"}`} data-testid="entry-total">{TRY(totals.total)}</span></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving || !form} data-testid="save-entry">{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={resolveScan} continuous />
    <NewProductQuickDialog open={np.open} barcode={np.barcode} onOpenChange={(o) => setNp((s) => ({ ...s, open: o }))} onCreated={(p) => addOrIncrementFromScan(p)} />
    </>
  );
}
